/**
 * patientPanel — the Patient Panel's state (Schedule View, Phase 7).
 *
 * Plan: docs/plans/2026-09-19-schedule-view-plan.md ("Patient Panel", Phase 7)
 *
 * The panel is a slide-over that shows one patient without leaving the page. It is STORE-driven,
 * not prop-driven, on purpose: today it is opened from a Schedule View block, but the plan moves
 * its host to Layout.vue so the header patient search can open it too. Anything that holds "which
 * patient is open" in a page component would have to be rewritten for that move.
 *
 * What lives HERE: which patient is open, and that patient's data — fetched by this module.
 * What does NOT: the live appointment, the alert payload and the office's alert settings. Those
 * belong to the host (Schedule View already keeps them live) and reach the panel as props, so an
 * appointment that turns "Arrived" while the panel is open updates in the panel too.
 *
 * Data sources (the Phase 7 inventory of Patient Profile, 2026-09-25):
 *   POST /schedule-view/patient                 Patient Profile's own SQL, test-pinned copy
 *   insuranceVerification/fetchPatientStatus    the same action Patient Profile's chip uses
 *   POST /get-tx-signed-plans-summary           the same endpoint as Patient Profile's section —
 *                                               called directly, NOT through txPresenter's action,
 *                                               which writes one shared list for the whole app
 *   tasks/getTasksTaggedWithPatient             the same Firestore listener as PatientTasksCard
 * No /runquery. Everything here is read-only.
 */

import axios from 'axios';
import { extractApiError } from '@/utils/apiError';

/**
 * Sequence numbers for "is this response still for the patient on screen?". Module-level rather
 * than state: they are bookkeeping, and a counter in state would re-render the panel on every
 * request for no visible change.
 */
let patientSeq = 0;
let insuranceSeq = 0;
let plansSeq = 0;
let tasksToken = 0;
/** The live Firestore unsubscribe for the open patient's tasks, or null. */
let tasksUnsubscribe = null;

export const PANEL_TABS = [
  'overview', 'followups', 'treatment', 'appointments', 'communication', 'financials', 'forms', 'tasks',
];

/** A tab the panel really has. Anything else (a typo, a tab removed later) is not one. */
function isPanelTab(tab) {
  return typeof tab === 'string' && PANEL_TABS.indexOf(tab) !== -1;
}

function initialState() {
  return {
    isOpen: false,
    /** The open patient, numeric. Named `openPatNum` — see the mixin prop-shadowing rule. */
    openPatNum: null,
    /** The appointment the panel was opened from, if any — the host resolves it live by AptNum. */
    openAptNum: null,
    /** "Today" in the site's timezone, YYYY-MM-DD, as the host resolved it. */
    today: null,
    tab: 'overview',
    /** Which optional sources this site has; a false one is never fetched. */
    capabilities: { insverify: true, txsign: true },

    data: null,
    dataLoading: false,
    dataError: '',
    unavailable: null,

    insurance: null,
    insuranceLoading: false,

    signedPlans: [],
    signedPlansLoading: false,
  };
}

export default {
  namespaced: true,

  state: initialState(),

  getters: {
    /**
     * The open patient's tasks, from the tasks module's shared slot.
     *
     * ⛔ Filtered to THIS patient, defensively, exactly as PatientTasksCard does: that slot is
     * shared, and PracticePulse's CheckoutNotes writes it and never tears its listener down, so a
     * stale listener can overwrite it with another patient's tasks.
     */
    tasks: (state, getters, rootState) => {
      const n = Number(state.openPatNum);
      const all = (rootState.tasks && rootState.tasks.tasksTaggedWithPatient) || [];
      if (!n) return [];
      return all.filter(t => Array.isArray(t.taggedPatients) && t.taggedPatients.includes(n));
    },
  },

  mutations: {
    setOpen(state, { patNum, aptNum, today, capabilities, tab }) {
      Object.assign(state, initialState());
      state.isOpen = true;
      state.openPatNum = patNum;
      state.openAptNum = aptNum === undefined ? null : aptNum;
      state.today = today || null;
      state.capabilities = Object.assign({ insverify: true, txsign: true }, capabilities || {});
      // ⛔ Validated here too, not only in `open`: this is the line that would otherwise let a stray
      // string become the tab, and the panel would render no body at all.
      state.tab = isPanelTab(tab) ? tab : 'overview';
    },
    setAppointment(state, aptNum) {
      state.openAptNum = aptNum === undefined ? null : aptNum;
    },
    setClosed(state) {
      Object.assign(state, initialState());
    },
    setTab(state, tab) {
      if (PANEL_TABS.indexOf(tab) !== -1) state.tab = tab;
    },
    setData(state, data) { state.data = data; },
    setDataLoading(state, v) { state.dataLoading = v; },
    setDataError(state, msg) { state.dataError = msg || ''; },
    setUnavailable(state, v) { state.unavailable = v || null; },
    setInsurance(state, v) { state.insurance = v; },
    setInsuranceLoading(state, v) { state.insuranceLoading = v; },
    setSignedPlans(state, v) { state.signedPlans = Array.isArray(v) ? v : []; },
    setSignedPlansLoading(state, v) { state.signedPlansLoading = v; },
  },

  actions: {
    /**
     * Open the panel on one patient and start loading everything about them.
     *
     * @param {object} p
     * @param {number|string} p.patNum
     * @param {number} [p.aptNum]         the appointment it was opened from
     * @param {string} p.today            YYYY-MM-DD in the SITE's timezone
     * @param {object} [p.capabilities]   { insverify, txsign } — false skips that fetch
     * @param {string} [p.tab]            the tab to land on (a PANEL_TABS entry — the visit checklist's
     *                                    category arrows pass one). Missing or not a real tab ⇒ Overview.
     */
    open({ commit, dispatch, state }, { patNum, aptNum, today, capabilities, tab } = {}) {
      const n = Number(patNum);
      if (!Number.isInteger(n) || n <= 0) {
        console.warn('[patientPanel] open called without a valid patNum');
        return;
      }
      // The same patient again (a second block for someone booked twice, or a double tap) keeps
      // what is already loaded and only re-points the appointment — no refetch, no flash.
      if (state.isOpen && state.openPatNum === n) {
        commit('setAppointment', aptNum);
        // ⛔ …but a REQUESTED tab still applies. The checklist's "Forms" arrow on a patient whose
        // panel is already open must land on Forms; returning early above is what would silently
        // drop it and leave the user on whatever tab they were reading. With no (or an unknown) tab
        // the user's place is kept — a plain click on a second block must not yank them to Overview.
        if (isPanelTab(tab)) commit('setTab', tab);
        return;
      }
      commit('setOpen', { patNum: n, aptNum, today, capabilities, tab });
      dispatch('fetchPatient');
      dispatch('fetchInsurance');
      dispatch('fetchSignedPlans');
      dispatch('watchTasks');
    },

    close({ commit, dispatch }) {
      // Invalidate everything in flight: a response landing after close must not repaint a
      // panel that is no longer there, or leave data behind for the next patient.
      patientSeq += 1;
      insuranceSeq += 1;
      plansSeq += 1;
      dispatch('unwatchTasks');
      commit('setClosed');
    },

    setTab({ commit }, tab) {
      commit('setTab', tab);
    },

    async fetchPatient({ commit, state, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const patNum = state.openPatNum;
      if (!siteID || !patNum || !state.today) return null;

      const seq = ++patientSeq;
      commit('setDataLoading', true);
      commit('setDataError', '');
      try {
        const response = await axios.post(
          '/schedule-view/patient',
          { siteID, patNum, date: state.today },
          { crossdomain: true }
        );
        if (seq !== patientSeq) return null;
        if (response.data && response.data.success) {
          const data = response.data.data || {};
          commit('setUnavailable', data.unavailable);
          commit('setData', data);
          return data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load this patient');
      } catch (err) {
        if (seq !== patientSeq) return null;
        console.error('[patientPanel] fetchPatient error:', err);
        commit('setDataError', extractApiError(err, 'Could not load this patient. Please try again.'));
        return null;
      } finally {
        if (seq === patientSeq) commit('setDataLoading', false);
      }
    },

    async fetchInsurance({ commit, dispatch, state, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const patNum = state.openPatNum;
      if (!siteID || !patNum || !state.capabilities.insverify) return null;
      const seq = ++insuranceSeq;
      commit('setInsuranceLoading', true);
      try {
        // Returns null on any error by design (the Patient Profile chip must never break a page).
        const data = await dispatch('insuranceVerification/fetchPatientStatus', { siteID, patNum }, { root: true });
        if (seq !== insuranceSeq) return null;
        // `enabled: false` is the soft-gate answer for a site without the feature.
        commit('setInsurance', data && data.enabled ? data : null);
        return data;
      } finally {
        if (seq === insuranceSeq) commit('setInsuranceLoading', false);
      }
    },

    async fetchSignedPlans({ commit, state, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const patNum = state.openPatNum;
      if (!siteID || !patNum || !state.capabilities.txsign) return null;
      const seq = ++plansSeq;
      commit('setSignedPlansLoading', true);
      try {
        const response = await axios.post('/get-tx-signed-plans-summary', { siteID, patNum }, { crossdomain: true });
        if (seq !== plansSeq) return null;
        const plans = (response.data && response.data.plans) || [];
        commit('setSignedPlans', plans);
        return plans;
      } catch (err) {
        if (seq !== plansSeq) return null;
        // Non-fatal, as on Patient Profile: the section shows its empty state.
        console.error('[patientPanel] fetchSignedPlans error:', err);
        commit('setSignedPlans', []);
        return null;
      } finally {
        if (seq === plansSeq) commit('setSignedPlansLoading', false);
      }
    },

    /**
     * Follow the open patient's tagged tasks live — the same listener as PatientTasksCard, torn
     * down on close and whenever the patient changes. The token catches a subscribe that resolves
     * after a newer one started: its listener is released, never kept.
     */
    async watchTasks({ dispatch, commit, state, rootState }) {
      dispatch('unwatchTasks');
      const siteID = rootState.customer && rootState.customer.siteID;
      const n = Number(state.openPatNum);
      if (!siteID || !n) return;
      const token = ++tasksToken;
      let unsub;
      try {
        unsub = await dispatch('tasks/getTasksTaggedWithPatient', { siteID, patientToLookFor: n }, { root: true });
      } catch (err) {
        console.error('[patientPanel] watchTasks error:', err);
        return;
      }
      if (token !== tasksToken) {
        if (typeof unsub === 'function') {
          unsub();
          commit('customer/removeFirestoreListener', unsub, { root: true });
        }
        return;
      }
      tasksUnsubscribe = typeof unsub === 'function' ? unsub : null;
    },

    unwatchTasks({ commit }) {
      tasksToken += 1;
      if (typeof tasksUnsubscribe === 'function') {
        tasksUnsubscribe();
        commit('customer/removeFirestoreListener', tasksUnsubscribe, { root: true });
      }
      tasksUnsubscribe = null;
      commit('tasks/resetTasksTaggedWithPatient', null, { root: true });
    },
  },
};
